import { store } from '../store';
import {
  applyDataMonitoringSnapshot,
  setSelectedInterval,
  setSelectedIntervalMessages,
  updateLatencyStats,
} from '../store/monitoringSlice';
import timeSeriesProcessor from './TimeSeriesProcessor';
import monitoringMessageBuffer from './MonitoringMessageBuffer';

/**
 * Monitoring Data Processor
 * Processes WebSocket messages for monitoring dashboard and updates Redux state
 */
class MonitoringDataProcessor {
  constructor() {
    this.intervalMs = 1000; // 1 second chart buckets
    this.processingIntervalMs = 500; // Flush incoming messages twice per second
    this.processingInterval = null;
    this.isProcessing = false;
    this.lastProcessedSequence = 0;
  }

  /**
   * Start processing monitoring data
   */
  start() {
    if (this.processingInterval) {
      if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_MONITORING === 'true') {
        console.log('MonitoringDataProcessor: Already running, skipping start');
      }
      return; // Already running
    }

    if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_MONITORING === 'true') {
      console.log('MonitoringDataProcessor: Starting...');
    }

    this.initializeProcessing();

    // Process data immediately
    this.processData();

    // Set up a faster processing loop than the chart bucket size so UI state stays near live.
    this.processingInterval = setInterval(() => {
      this.processData();
    }, this.processingIntervalMs);

    if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_MONITORING === 'true') {
      console.log('MonitoringDataProcessor: Started processing with interval', this.processingIntervalMs, 'ms');
    }
  }

  initializeProcessing() {
    // Set interval size for time series processor (only clears data if interval changes)
    timeSeriesProcessor.setIntervalSize(this.intervalMs);

    // Initialize the display window without using browser time as a message cutoff.
    timeSeriesProcessor.initializeRealTime();
  }

  /**
   * Stop processing monitoring data
   */
  stop() {
    if (this.processingInterval) {
      clearInterval(this.processingInterval);
      this.processingInterval = null;
      if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_MONITORING === 'true') {
        console.log('MonitoringDataProcessor: Stopped processing');
      }
    }
  }

  /**
   * Process current messages and update Redux state
   */
  processData() {
    if (this.isProcessing) {
      if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_MONITORING === 'true') {
        console.log('MonitoringDataProcessor: Already processing, skipping...');
      }
      return; // Prevent concurrent processing
    }

    this.isProcessing = true;

    try {
      const recentMessageCount = monitoringMessageBuffer.getMessageCount();
      const {
        messages: newMessages,
        nextSequence
      } = monitoringMessageBuffer.getMessagesAfter(this.lastProcessedSequence);

      if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_MONITORING === 'true') {
        const state = store.getState();
        console.log(`MonitoringDataProcessor.processData called - messages: ${recentMessageCount}, sources: ${state.sources?.sources?.length || 0}`);
      }

      if (recentMessageCount === 0) {
        if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_MONITORING === 'true') {
          console.log('MonitoringDataProcessor: No messages to process');
        }
        this.isProcessing = false;
        return;
      }

      this.lastProcessedSequence = nextSequence;
      this.processBatch(newMessages, recentMessageCount);

    } catch (error) {
      console.error('MonitoringDataProcessor: Error processing data:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  processBatch(newMessages, recentMessageCount = 0, currentTime = Date.now()) {
    try {
      if (newMessages.length === 0) {
        // No new messages - don't update Redux state unnecessarily
        // This prevents components from re-rendering when there's no new data
        if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_MONITORING === 'true') {
          console.log('MonitoringDataProcessor: No new messages, skipping Redux update');
        }
        return;
      }

      // Only log if processing more than expected (indicates a problem)
      if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_MONITORING === 'true' && newMessages.length > 5) {
        console.log(`MonitoringDataProcessor: WARNING - Processing ${newMessages.length} messages at once (total: ${recentMessageCount}, sequence: ${this.lastProcessedSequence})`);
      }

      const state = store.getState();
      const { sources } = state.sources;
      const now = currentTime;

      // Create a map of source IDs to source names for quick lookup
      const sourceMap = {};
      if (sources && sources.length > 0) {
        sources.forEach(source => {
          sourceMap[source.source_id] = source.name;
        });
      }

      // Helper function to get source name
      const getSourceName = (sourceId) => {
        return sourceMap[sourceId] || `Unknown Source (${sourceId})`;
      };

      // Process NEW messages for time series data
      const processedMessages = newMessages.map(message => {
        const messageId = message.id || `msg_${Date.now()}_${Math.random()}`;
        const sourceId = message.leader_source || message.source_id || 'unknown';
        const sourceName = getSourceName(sourceId);

        // Handle different timestamp formats from websocket messages
        let timestamp = now; // Default to current time
        if (message.leader_timestamp) {
          // New format: "2025-06-10 06:48:23 UTC"
          timestamp = new Date(message.leader_timestamp).getTime();
        } else if (message.timestamp) {
          // Legacy format
          timestamp = new Date(message.timestamp).getTime();
        }

        return {
          ...message,
          messageId,
          sourceId,
          sourceName,
          timestamp
        };
      });

      const sourceTrackingSnapshot = this.getSourceTrackingSnapshot(processedMessages, now);

      // Update time series processor with new data
      timeSeriesProcessor.processMessages(processedMessages, now);

      // Get formatted chart data (use 1 hour window)
      const chartData = timeSeriesProcessor.getFormattedChartData(60 * 60 * 1000); // 1 hour

      // Compute rolling 60s message rate from raw buckets (each bucket = this.intervalMs)
      const rawBuckets = timeSeriesProcessor.getTimeSeriesData(60 * 1000);
      const completeBuckets = rawBuckets.filter(d => d.timestamp < now - this.intervalMs);
      const messageRate = completeBuckets.length > 0
        ? completeBuckets.reduce((sum, d) => sum + (d.total || 0), 0) / (completeBuckets.length * this.intervalMs / 1000)
        : null;

      store.dispatch(applyDataMonitoringSnapshot({
        timeSeriesData: chartData,
        lastUpdateTime: now,
        messageRate,
        ...sourceTrackingSnapshot
      }));

      const latencySamples = processedMessages
        .map(m => m.leader_latency_ms)
        .filter(v => typeof v === 'number' && isFinite(v));
      if (latencySamples.length > 0) {
        store.dispatch(updateLatencyStats(latencySamples));
      }

    } catch (error) {
      console.error('MonitoringDataProcessor: Error processing batch:', error);
    }
  }

  getSourceTrackingSnapshot(messages, currentTime) {
    const state = store.getState();
    const currentSourceOrder = state.monitoring?.persistentSourceOrder || [];
    const currentSourceLastSeen = state.monitoring?.sourceLastSeen || {};
    const currentChannelLastSeen = state.monitoring?.channelLastSeen || {};
    const sourceActivityTimeout = 60 * 60 * 1000;

    const nextSourceOrder = [...currentSourceOrder];
    const nextSourceOrderSet = new Set(nextSourceOrder);
    const nextSourceLastSeen = { ...currentSourceLastSeen };
    const nextChannelLastSeen = { ...currentChannelLastSeen };
    let hasLastSeenChanges = false;
    let hasChannelLastSeenChanges = false;
    let hasOrderChanges = false;

    messages.forEach(message => {
      const sourceId = message.sourceId || 'unknown';
      const timestamp = Number(message.timestamp) || currentTime;

      const nextTimestamp = Math.max(nextSourceLastSeen[sourceId] || 0, timestamp);
      if (nextTimestamp !== nextSourceLastSeen[sourceId]) {
        nextSourceLastSeen[sourceId] = nextTimestamp;
        hasLastSeenChanges = true;
      }

      const channelId = message.channel;
      if (channelId) {
        const nextChannelTimestamp = Math.max(nextChannelLastSeen[channelId] || 0, currentTime);
        if (nextChannelTimestamp !== nextChannelLastSeen[channelId]) {
          nextChannelLastSeen[channelId] = nextChannelTimestamp;
          hasChannelLastSeenChanges = true;
        }
      }

      if (!nextSourceOrderSet.has(sourceId)) {
        nextSourceOrder.push(sourceId);
        nextSourceOrderSet.add(sourceId);
        hasOrderChanges = true;
      }
    });

    const activeSourceOrder = nextSourceOrder.filter(sourceId => {
      const lastSeen = nextSourceLastSeen[sourceId];
      return lastSeen && (currentTime - lastSeen) < sourceActivityTimeout;
    });

    const orderChanged = hasOrderChanges ||
      activeSourceOrder.length !== currentSourceOrder.length ||
      !activeSourceOrder.every((id, index) => id === currentSourceOrder[index]);

    const snapshot = {};

    if (hasLastSeenChanges) {
      snapshot.sourceLastSeen = nextSourceLastSeen;
    }

    if (hasChannelLastSeenChanges) {
      snapshot.channelLastSeen = nextChannelLastSeen;
    }

    if (orderChanged) {
      snapshot.persistentSourceOrder = activeSourceOrder;
    }

    return snapshot;
  }

  /**
   * Handle chart point click - find messages in the selected interval
   */
  getSourceNameLookup(sources) {
    const sourceMap = {};
    if (sources && sources.length > 0) {
      sources.forEach(source => {
        sourceMap[source.source_id] = source.name;
      });
    }

    return (sourceId) => {
      return sourceMap[sourceId] || `Unknown Source (${sourceId})`;
    };
  }

  formatMessagesForDetails(messages, sources) {
    const getSourceName = this.getSourceNameLookup(sources);

    return messages.map(msg => {
      const messageId = msg.id || `msg_${Date.now()}_${Math.random()}`;
      const sourceId = msg.leader_source || msg.source_id || 'unknown';
      const sourceName = getSourceName(sourceId);

      // Handle different timestamp formats from websocket messages
      let timestamp = Date.now(); // Default to current time
      if (msg.leader_timestamp) {
        // New format: "2025-06-10 06:48:23 UTC"
        timestamp = new Date(msg.leader_timestamp).getTime();
      } else if (msg.timestamp) {
        // Legacy format
        timestamp = new Date(msg.timestamp).getTime();
      }

      return {
        ...msg, // Include original message for additional details
        messageId,
        sourceId,
        sourceName,
        timestamp,
        leader_id: msg.leader_id,
        record_type: msg.type
      };
    });
  }

  handlePointClick(point, sources) {
    try {
      if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_MONITORING === 'true') {
        console.log('MonitoringDataProcessor.handlePointClick called with:', point);
      }

      const messageCount = monitoringMessageBuffer.getMessageCount();

      if (!point || messageCount === 0) {
        console.log('No point or messages available:', { point: !!point, messagesCount: messageCount });
        return;
      }

      if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_MONITORING === 'true') {
        console.log('Point and messages are available, proceeding with search...');
        console.log('Messages array length:', messageCount);
        console.log('Point data:', point);
      }

      // Find messages in this interval
      const intervalStart = point.timestamp;
      const intervalEnd = intervalStart + (point.bucketDuration || this.intervalMs);

      if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_MONITORING === 'true') {
        console.log(`Searching for messages in interval: ${new Date(intervalStart).toISOString()} - ${new Date(intervalEnd).toISOString()}`);
        console.log(`Total messages in monitoring buffer: ${messageCount}`);
        console.log(`Target interval: ${new Date(intervalStart).toISOString()} (${intervalStart}) to ${new Date(intervalEnd).toISOString()} (${intervalEnd})`);
      }

      const messagesInInterval = this.formatMessagesForDetails(
        monitoringMessageBuffer.getMessagesInInterval(intervalStart, intervalEnd),
        sources
      );

      if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_MONITORING === 'true') {
        messagesInInterval.forEach(msg => {
          const msgTimestamp = monitoringMessageBuffer.parseMessageTimestamp(msg);

          console.log(`Message ${msg.id} matches interval:`, {
            msgTimestamp: new Date(msgTimestamp).toISOString(),
            intervalStart: new Date(intervalStart).toISOString(),
            intervalEnd: new Date(intervalEnd).toISOString()
          });
        });
      }

      // Update Redux state
      store.dispatch(setSelectedInterval(point));
      store.dispatch(setSelectedIntervalMessages(messagesInInterval));

      if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_MONITORING === 'true') {
        console.log(`Found ${messagesInInterval.length} messages in interval ${new Date(intervalStart).toISOString()} - ${new Date(intervalEnd).toISOString()}`);
        if (messagesInInterval.length > 0) {
          console.log('Sample messages:', messagesInInterval.slice(0, 2));
        }
      }

    } catch (error) {
      console.error('MonitoringDataProcessor: Error handling point click:', error);
    }
  }

  /**
   * Handle chart range selection - find messages in the selected interval
   */
  handleRangeSelect(range, sources) {
    try {
      if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_MONITORING === 'true') {
        console.log('MonitoringDataProcessor.handleRangeSelect called with:', range);
      }

      const messageCount = monitoringMessageBuffer.getMessageCount();
      const intervalStart = Number(range?.startTimestamp);
      const intervalEnd = Number(range?.endTimestamp);

      if (!Number.isFinite(intervalStart) || !Number.isFinite(intervalEnd) || intervalEnd <= intervalStart || messageCount === 0) {
        console.log('No valid range or messages available:', {
          hasRange: !!range,
          messagesCount: messageCount,
          intervalStart,
          intervalEnd
        });
        return;
      }

      const messagesInInterval = this.formatMessagesForDetails(
        monitoringMessageBuffer.getMessagesInInterval(intervalStart, intervalEnd),
        sources
      );

      store.dispatch(setSelectedInterval({
        timestamp: intervalStart,
        startTimestamp: intervalStart,
        endTimestamp: intervalEnd,
        bucketDuration: range.bucketDuration || this.intervalMs,
        isRange: true
      }));
      store.dispatch(setSelectedIntervalMessages(messagesInInterval));

      if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_MONITORING === 'true') {
        console.log(`Found ${messagesInInterval.length} messages in range ${new Date(intervalStart).toISOString()} - ${new Date(intervalEnd).toISOString()}`);
      }
    } catch (error) {
      console.error('MonitoringDataProcessor: Error handling range selection:', error);
    }
  }

  /**
   * Clear all monitoring data
   */
  clearData() {
    timeSeriesProcessor.clearData();
    monitoringMessageBuffer.clear();
    this.lastProcessedSequence = 0;
    // Reinitialize for real-time processing after clearing
    if (this.processingInterval) {
      timeSeriesProcessor.initializeRealTime();
    }
    // Redux state will be cleared by the component
  }

  /**
   * Get interval duration in milliseconds
   */
  getIntervalMs() {
    return this.intervalMs;
  }

  /**
   * Get debug information
   */
  getDebugInfo() {
    return {
      isRunning: !!this.processingInterval,
      intervalMs: this.intervalMs,
      processingIntervalMs: this.processingIntervalMs,
      isProcessing: this.isProcessing,
      lastProcessedSequence: this.lastProcessedSequence,
      timeSeriesProcessor: timeSeriesProcessor.getDebugInfo()
    };
  }
}

// Create singleton instance
const monitoringDataProcessor = new MonitoringDataProcessor();

export default monitoringDataProcessor;
